import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Topbar from "@/components/Topbar";
import {
  EnvelopeSimple, Copy, ArrowSquareOut, CheckCircle,
  WarningCircle, LinkSimple, Prohibit,
} from "@phosphor-icons/react";

/* v2.97 · Vendor Inquiries page — one-click send + credential
   readiness matrix for the vendor onboarding workflow.
   Route: /admin/vendor-inquiries (admin-gated in App.js). */

const DRAFTS = [
  {
    slug: "abb",
    name: "ABB Electrification",
    to:      "digital.support@abb.com",
    portal:  "https://developer.abb.com",
    subject: "API partner integration request — EEngDeS (electrical design SaaS)",
    body: `Hi ABB Digital / Ability team,

I run product at EEngDeS — a US-based SaaS marketplace + design platform for Professional Engineers and industrial owner-operators that helps our clients specify, size, procure and stamp electrical distribution equipment (MCCs, panelboards, transformers, drives, breakers, arc-flash gear).

We would like to become an integration partner for the ABB Electrification catalog so our platform can:

1. Populate SKU pick-lists inside our Power System Studies (short-circuit, TCC coordination, harmonics, arc-flash) with real ABB catalog numbers instead of generic device classes.
2. Auto-generate Bill of Materials for MCC and panelboard designs.
3. Embed ABB datasheets and product images in the PE-stamped deliverables our engineers produce for their clients.
4. Route qualified end-of-line RFQs to the ABB channel — we do NOT resell your products.

Specifically we'd like sandbox access to the following scopes:

- catalog.read — search + retrieve product metadata
- catalog.datasheet.read — signed URLs for datasheet PDFs
- pricing.list.read — list price + lead time
- configurator.tmax — Tmax XT / Emax 2 breaker selection
- configurator.transformer — dry-type / oil-filled sizing
- iec-relay-curves.read — Relion 615 IEC TCC curves
- mv-switchgear.selector — UniGear / SafeGear MV selection
- spares-and-obsolescence.read — auto-flag EOL products
- partner-pricing.hierarchy — respect our clients' partner tier

Expected volume year 1: ~5K API calls/day → ~50K/day by year 3.
Target integration timeline: sandbox in 30 days, production in ~90.

We already have an OAuth2 client-credentials adapter shipped for a peer vendor and can complete the ABB adapter within one sprint of receiving sandbox credentials. Happy to sign the standard ABB NDA / channel-partner agreement in parallel.

Primary technical contact:
  <YOUR NAME>
  <YOUR EMAIL>
  <YOUR EEngDeS URL>

Thanks — looking forward to speaking.`,
  },
  {
    slug: "siemens",
    name: "Siemens Xcelerator",
    to:      "developer.support@siemens.com",
    portal:  "https://developer.siemens.com",
    subject: "API World partner request — EEngDeS (engineering SaaS)",
    body: `Hi Siemens API World team,

I'm the product lead at EEngDeS, a US-based SaaS platform where Professional Engineers and industrial owner-operators specify, size and stamp electrical distribution equipment. We would like to onboard as an API partner so our platform can surface real Siemens Smart-Infrastructure SKUs across our design and power-study flows.

We are specifically interested in the following API products (all listed under Smart Infrastructure — Electrical Products on developer.siemens.com):

- SENTRON Product Data API — 3VA, 3WL breakers; 7KM PAC meters
- SIRIUS Product Data API — 3RT / 3RV / soft-starter selection
- SIVACON Configurator API — LV switchboard configuration
- SIMOSC / SINAMICS Drive Selector API — VFD sizing
- SIPROTEC Relay Curve Library — for our TCC coordinator
- Industry Mall Product Search + Datasheet + Pricing — the core catalog we need

We would like sandbox credentials first (via the API World "test / start for free" flow if available), then move to production after our integration passes an internal review. Our clients' AL customer numbers will be kept in their profile so we can request per-customer contracted pricing via the AL Reference header.

Volume estimate year 1: ~5K calls/day, growing to ~50K/day.

Primary technical contact:
  <YOUR NAME>
  <YOUR EMAIL>
  <YOUR EEngDeS URL>

Happy to jump on a discovery call at your convenience.`,
  },
  {
    slug: "eaton",
    name: "Eaton Developer Portal",
    to:      "developer.support@eaton.com",
    portal:  "https://developer.eaton.com",
    subject: "Developer Portal API access — EEngDeS (electrical design SaaS)",
    body: `Hi Eaton developer relations team,

I run product at EEngDeS, a US-based SaaS marketplace + design platform for Professional Engineers and industrial owner-operators. We help our customers specify, size, procure and stamp electrical distribution equipment (MCCs, panelboards, transformers, drives, breakers, fuses, arc-flash gear).

I registered on developer.eaton.com as <YOUR EMAIL> and would like to request access to the following API products from the API Specification Catalog:

- Product Catalog API (Cutler-Hammer + Bussmann) — full LV/MV breaker + fuse + panelboard catalog
- Bill of Materials Configurator API — Xpert Assembler-equivalent
- Channel Inventory Enablement API — real-time distributor stock
- CROUSE-HINDS + B-Line accessory catalog — cable trays, enclosures, hazardous-location fittings
- Bussmann Fuse Selector — for our TCC coordinator

Sandbox credentials would let us complete our OAuth2 (Base64 Authorization + /oauth/accesstoken — TTL 1 hr) adapter and validate the response shape end-to-end.

Expected volume: ~5K API calls/day year 1, ~50K/day year 3.

Primary technical contact:
  <YOUR NAME>
  <YOUR EMAIL>
  <YOUR EEngDeS URL>

Looking forward to enabling the integration — happy to sign the standard Eaton API terms in parallel.`,
  },
];

// -- Vendor readiness matrix --------------------------------------

function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
      ok ? "bg-[#7CFF6B]/15 text-[#7CFF6B]" : "bg-white/5 text-gray-500"}`}>
      {ok ? <CheckCircle size={11} weight="fill" /> : <Prohibit size={11} />}
      {label}
    </span>
  );
}

function ReadinessMatrix() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vendors/status");
        setRows(data.items || []);
      } catch (e) { setRows([]); }
    })();
  }, []);

  if (rows === null) return (
    <div className="text-xs font-mono text-gray-500 p-4">loading vendor readiness…</div>
  );

  return (
    <div className="border border-white/10" data-testid="vendor-readiness">
      <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/10 text-gray-400 uppercase text-[9px] tracking-widest font-mono">
        <div className="col-span-2">vendor</div>
        <div className="col-span-1">mode</div>
        <div className="col-span-2">enable flag</div>
        <div className="col-span-5">credentials</div>
        <div className="col-span-2 text-right">status</div>
      </div>
      {rows.map((r) => (
        <div key={r.slug}
          className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/5 items-center font-mono text-xs"
          data-testid={`vendor-readiness-row-${r.slug}`}>
          <div className="col-span-2 text-white uppercase">{r.slug}</div>
          <div className="col-span-1">
            <StatusPill ok={r.mode === "live"} label={r.mode} />
          </div>
          <div className="col-span-2 text-gray-400 text-[10px]">
            {r.enable_flag}={r.flag_set ? <span className="text-[#7CFF6B]">true</span> : <span className="text-gray-500">—</span>}
          </div>
          <div className="col-span-5 flex flex-wrap gap-1">
            {(r.creds_required || []).map((k) => (
              <span key={k}
                className={`px-1.5 py-0.5 text-[9px] ${
                  r.creds_present?.[k] ? "bg-[#7CFF6B]/15 text-[#7CFF6B]" : "bg-white/5 text-gray-500"}`}
                title={r.creds_present?.[k] ? "set in .env" : "missing — set in backend/.env"}>
                {k}
              </span>
            ))}
          </div>
          <div className="col-span-2 text-right">
            {r.ready_to_flip
              ? (r.mode === "live"
                  ? <StatusPill ok label="LIVE" />
                  : <StatusPill ok label="READY" />)
              : <span className="inline-flex items-center gap-1 text-[10px] text-[#FFCC00]">
                  <WarningCircle size={11} weight="fill" /> waiting on keys
                </span>}
          </div>
        </div>
      ))}
      <div className="px-3 py-2 text-[10px] font-mono text-gray-500 bg-[#12151C]">
        To flip a vendor to LIVE: set every listed cred in
        <span className="text-[#00E5FF]"> /app/backend/.env</span>,
        add <span className="text-[#00E5FF]">&lt;VENDOR&gt;_ENABLED=true</span>,
        then restart the backend and re-check this page.
      </div>
    </div>
  );
}

// -- Inquiry cards -------------------------------------------------

function InquiryCard({ draft, idx }) {
  const [sent, setSent] = useState(false);
  const mailtoUrl = `mailto:${encodeURIComponent(draft.to)}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${draft.subject}\nTo: ${draft.to}\n\n${draft.body}`);
      toast.success("Copied full draft to clipboard");
    } catch (e) {
      toast.error("Clipboard blocked — pick text manually");
    }
  };
  const openMail = () => {
    window.location.href = mailtoUrl;
    setSent(true);
  };

  return (
    <div className="border border-white/10 bg-[#12151C] p-4" data-testid={`inquiry-card-${draft.slug}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="label-tag text-[9px]">// draft {idx + 1}/3</div>
          <h3 className="font-display text-xl font-bold mt-0.5">{draft.name}</h3>
          <div className="flex items-center gap-3 mt-1 text-[10px] font-mono text-gray-500">
            <span className="inline-flex items-center gap-1">
              <EnvelopeSimple size={11} /> {draft.to}
            </span>
            <a href={draft.portal} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[#00E5FF] hover:underline">
              <LinkSimple size={11} /> portal
            </a>
          </div>
        </div>
        {sent && (
          <span className="inline-flex items-center gap-1 text-[10px] text-[#7CFF6B]"
                data-testid={`inquiry-sent-${draft.slug}`}>
            <CheckCircle size={12} weight="fill" /> opened in mail app
          </span>
        )}
      </div>

      <div className="text-[10px] font-mono text-gray-500 mb-1">// subject</div>
      <div className="text-sm text-white mb-3 border-l-2 border-[#00E5FF] pl-3">{draft.subject}</div>

      <div className="text-[10px] font-mono text-gray-500 mb-1">// body</div>
      <pre className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-300 font-mono border border-white/5 bg-[#0A0C10] p-3 max-h-[280px] overflow-auto">
{draft.body}
      </pre>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={openMail}
          className="btn-primary text-xs inline-flex items-center gap-1"
          data-testid={`inquiry-send-${draft.slug}`}
          title={`Opens your default mail client with the draft pre-filled to ${draft.to}`}>
          <ArrowSquareOut size={12} weight="fill" /> Open in mail client
        </button>
        <button onClick={copyBody}
          className="btn-ghost text-xs inline-flex items-center gap-1 py-1.5"
          data-testid={`inquiry-copy-${draft.slug}`}>
          <Copy size={12} /> Copy to clipboard
        </button>
        <span className="ml-auto text-[10px] font-mono text-gray-500">
          swap &lt;YOUR NAME&gt; / &lt;YOUR EMAIL&gt; / &lt;YOUR EEngDeS URL&gt; before sending
        </span>
      </div>
    </div>
  );
}

export default function AdminVendorInquiriesPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Topbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="label-tag">// admin · vendor onboarding</div>
          <h1 className="font-display text-4xl font-bold mt-2">Vendor inquiries & readiness</h1>
          <p className="text-sm text-gray-400 mt-2 max-w-3xl">
            Send the three vendor partner-inquiry drafts to ABB, Siemens
            and Eaton in one click each. Below the drafts, a live
            readiness matrix shows which credentials are already
            configured in <span className="font-mono text-[#00E5FF]">backend/.env</span> and
            which vendor is one env-flag flip away from going live.
          </p>
        </div>

        <div className="mb-6">
          <div className="label-tag">// readiness matrix</div>
          <ReadinessMatrix />
        </div>

        <div className="mb-4">
          <div className="label-tag">// drafts</div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
          {DRAFTS.map((d, i) => <InquiryCard key={d.slug} draft={d} idx={i} />)}
        </div>
      </div>
    </div>
  );
}
